import { useState } from "react";
import { Plus, Pencil, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { fmtDate, MONTHS } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, NoAccess, Badge, EmptyState } from "@/components/common/States";
import { CompanyFormDialog } from "./CompanyFormDialog";

const Detail = ({ label, value }) => (
  <div><div className="g39-label">{label}</div><div className="text-sm mt-1">{value || <span className="text-muted-foreground">Not set</span>}</div></div>
);

const CompanyProfile = ({ company, canEdit, onEdit }) => (
  <div className="g39-card p-6 sm:p-8" data-testid="company-profile">
    <div className="flex items-start justify-between gap-4 mb-6">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-accent flex items-center justify-center"><Building2 className="h-5 w-5 text-gold" /></div>
        <div><div className="font-display text-lg font-semibold">{company.name}</div><div className="text-xs font-mono-g text-muted-foreground">{company.code}</div></div>
      </div>
      {canEdit && <Button variant="outline" size="sm" onClick={onEdit} data-testid="edit-company-button"><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit</Button>}
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
      <Detail label="Legal name" value={company.legal_name} />
      <Detail label="GSTIN" value={company.gstin} />
      <Detail label="PAN" value={company.pan} />
      <Detail label="GST state code" value={company.state_code} />
      <Detail label="Address" value={[company.address, company.city, company.state, company.pincode].filter(Boolean).join(", ")} />
      <Detail label="Contact" value={[company.phone, company.email].filter(Boolean).join(" · ")} />
      <Detail label="Financial year starts" value={MONTHS[(company.fy_start_month || 4) - 1]} />
      <Detail label="Currency" value={company.currency} />
      <Detail label="Status" value={company.status} />
    </div>
  </div>
);

export default function CompaniesPage() {
  const { can, isPlatform, company, setCompany, reload: reloadSession } = useSession();
  const { data, loading, error, reload } = useFetch("/companies", null, can("company.view"));
  const [dialog, setDialog] = useState({ open: false, company: null });
  if (!can("company.view")) return <NoAccess perm="company.view" />;
  const onSaved = () => { reload(); reloadSession(); };

  return (
    <div data-testid="companies-page">
      <PageHeader
        eyebrow="Administration"
        title={isPlatform ? "Companies" : "Company profile"}
        subtitle={isPlatform ? "Every company is an isolated tenant with its own branches, staff and records." : "Business information and GST settings for your company."}
        actions={can("platform.manage") && <Button onClick={() => setDialog({ open: true, company: null })} data-testid="add-company-button"><Plus className="h-4 w-4 mr-1.5" />Onboard company</Button>}
      />
      {!isPlatform && company && <CompanyProfile company={company} canEdit={can("company.edit")} onEdit={() => setDialog({ open: true, company })} />}
      {isPlatform && (
        <div className="g39-card overflow-hidden">
          {loading && <LoadingRows />}
          {error && <ErrorState message={error} onRetry={reload} />}
          {data && !data.length && <EmptyState title="No companies yet" />}
          {data?.length > 0 && (
            <div className="overflow-x-auto">
              <Table data-testid="companies-table">
                <TableHeader><TableRow><TableHead>Company</TableHead><TableHead>GSTIN</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.map((c) => (
                    <TableRow key={c.id} data-testid={`company-row-${c.code}`}>
                      <TableCell><div className="font-medium">{c.name} {c.is_demo && <Badge tone="demo" className="ml-1">Demo</Badge>}</div><div className="text-xs font-mono-g text-muted-foreground">{c.code}</div></TableCell>
                      <TableCell className="font-mono-g text-xs">{c.gstin || "—"}</TableCell>
                      <TableCell><Badge tone={c.status}>{c.status}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDate(c.created_at)}</TableCell>
                      <TableCell className="text-right space-x-2 whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => setCompany(c.id)} data-testid={`switch-to-company-${c.code}`}>Switch to</Button>
                        <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, company: c })} data-testid={`edit-company-${c.code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
      <CompanyFormDialog open={dialog.open} company={dialog.company} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={onSaved} />
    </div>
  );
}
